import { getAuth } from "firebase/auth";
import {
  type DocumentData,
  doc,
  getDoc,
  setDoc,
  updateDoc,
} from "firebase/firestore/lite";
import type { FormikValues } from "formik";
import { db } from "../auth/firebase";

interface User {
  userId?: string;
  handle?: string;
  description?: string;
  postcode?: string;
  age?: number | string;
  gender?: string;
  newsletter?: boolean;
  providerId?: string;
  [key: string]: any;
}

interface Project {
  organizationId?: string;
  projectId?: string;
  settings?: {
    demographicData?: {
      fields?: Array<{
        name: string;
        type: string;
        label: string;
        options?: Array<{ value: string; label: string }>;
      }>;
    };
  };
}

export const useHandleSubmitEditDetails = (user: User) => {
  return async (
    formik: FormikValues,
    project?: Project,
  ): Promise<DocumentData | undefined> => {
    console.log("useHandleSubmitEditDetails", project);
    try {
      const authUserId = getAuth()?.currentUser?.uid;
      const userId = user?.userId || authUserId;
      if (!userId) {
        throw new Error("No userId available for updating user details");
      }

      // Create a clean user data object, only including defined values
      const updatedUserData: User = {};

      // Only add fields that have actual values (not null, undefined, or empty strings)
      if (
        formik.values.handle !== undefined &&
        formik.values.handle !== null &&
        formik.values.handle !== ""
      ) {
        updatedUserData.handle = formik.values.handle;
      }
      if (
        formik.values.description !== undefined &&
        formik.values.description !== null &&
        formik.values.description !== ""
      ) {
        updatedUserData.description = formik.values.description;
      }
      if (
        formik.values.postcode !== undefined &&
        formik.values.postcode !== null &&
        formik.values.postcode !== ""
      ) {
        updatedUserData.postcode = formik.values.postcode;
      }
      if (
        formik.values.age !== undefined &&
        formik.values.age !== null &&
        formik.values.age !== ""
      ) {
        updatedUserData.age = formik.values.age;
      }
      if (
        formik.values.gender !== undefined &&
        formik.values.gender !== null &&
        formik.values.gender !== ""
      ) {
        updatedUserData.gender = formik.values.gender;
      }
      if (
        formik.values.newsletter !== undefined &&
        formik.values.newsletter !== null
      ) {
        updatedUserData.newsletter = formik.values.newsletter;
      }
      if (
        formik.values.providerId !== undefined &&
        formik.values.providerId !== null &&
        formik.values.providerId !== ""
      ) {
        updatedUserData.providerId = formik.values.providerId;
      }

      // Update user document
      try {
        if (Object.keys(updatedUserData).length > 0) {
          await updateDoc(doc(db, "users", userId), updatedUserData);
        }
      } catch (error) {
        console.error("Error updating user document:", error);
        // Continue execution even if this update fails
      }

      let updatedUser: DocumentData = {};

      // Add demographic data fields if they exist in project settings
      if (
        project?.settings?.demographicData?.fields &&
        project.organizationId &&
        project.projectId
      ) {
        const demographicData: any = {};

        project.settings.demographicData.fields.forEach((field) => {
          const fieldValue = formik.values[field.name];
          if (
            fieldValue !== undefined &&
            fieldValue !== null &&
            fieldValue !== ""
          ) {
            if (field.type === "singlechoice" && field.options) {
              const selectedOption = field.options.find(
                (option) => option.value === fieldValue,
              );
              demographicData[field.name] = selectedOption
                ? selectedOption.value
                : fieldValue;
            } else {
              demographicData[field.name] = fieldValue;
            }
          }
        });

        console.log("updatedUserData", {
          ...updatedUserData,
          ...demographicData,
        });

        // Update participant document in the project
        try {
          const participantData = { ...updatedUserData, ...demographicData };
          if (Object.keys(participantData).length > 0) {
            // Use setDoc with merge option to create the document if it doesn't exist
            await setDoc(
              doc(
                db,
                `organizations/${project.organizationId}/projects/${project.projectId}/participants/${userId}`,
              ),
              participantData,
              { merge: true },
            );
          }

          // Get updated participant document
          const participantDoc = await getDoc(
            doc(
              db,
              `organizations/${project.organizationId}/projects/${project.projectId}/participants/${userId}`,
            ),
          );
          updatedUser = participantDoc.data() || {};
        } catch (error) {
          console.error("Error updating/fetching participant document:", error);
          // If this fails, fall back to the user document
          const userDoc = await getDoc(doc(db, "users", userId));
          updatedUser = userDoc.data() || {};
        }
      } else {
        // Get updated user document
        const userDoc = await getDoc(doc(db, "users", userId));
        updatedUser = userDoc.data() || {};
      }

      // Ensure the returned user object has the userId
      updatedUser.userId = userId;

      console.log("Final updated user data:", updatedUser);
      return updatedUser;
    } catch (error) {
      console.error("Error in useHandleSubmitEditDetails:", error);
      throw new Error("Failed to update user details");
    }
  };
};
